<template>
    <m-pageframe title="Bulk Message Recipient Imports">
        <v-container fluid style="padding: 15px">
                    <imports-search-card
                :card_title="'Bulk Recipient Import History'"
                endpoint_base="/api/v1/imports/bulk-message-recipients"
            >
                <template #actions>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-btn
                                    color="primary"
                                    :disabled="!hasWriteAccess()"
                                    @click="onClickStartImport"
                                >
                                    Import Recipients...
                                </v-btn>
                            </span>
                        </template>
                        <span v-if="!hasWriteAccess()">You do not have permission to create imports. Only organization administrators can create or modify imports.</span>
                        <span v-else>Import</span>
                    </v-tooltip>
                </template>

                <template #default="{ results, loading, footer_props, computed_no_data_text }">
                    <imports-data-table
                        v-if="results != null"
                        :headers="table_headers"
                        :items="results"
                        :items-per-page="50"
                        :loading="loading"
                        loading_text="Loading bulk recipient imports..."
                        :no_data_text="computed_no_data_text"
                        :footer_props="footer_props"
                        import_type="bulk-message-recipients"
                        upload_route_name="bulk-message-recipients-import-upload"
                    />
                </template>
            </imports-search-card>
        </v-container>

        <!-- Bulk Message Selection Dialog -->
        <v-dialog v-model="bulk_message_dialog" max-width="600px">
            <v-card>
                <v-card-title>
                    Select Bulk Message
                    <v-spacer></v-spacer>
                    <v-btn icon @click="bulk_message_dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <p class="mb-4">Choose which bulk message you want to import recipients into:</p>

                    <v-select
                        v-model="selected_bulk_message_id"
                        :items="bulk_message_options"
                        label="Bulk Message"
                        :loading="loading_bulk_message_lists"
                        :disabled="loading_bulk_message_lists"
                        item-text="text"
                        item-value="value"
                        outlined
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="bulk_message_dialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!selected_bulk_message_id"
                        @click="onConfirmListSelection"
                    >
                        Continue to Import
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </m-pageframe>
</template>

<script>
import ImportsSearchCard from '@/components/common/imports-search-card.vue';
import ImportsDataTable from '@/components/imports/imports-data-table.vue';

export default {
    components: { ImportsSearchCard, ImportsDataTable },
    name: 'SearchBulkMessageRecipientImports',


    data() {
        return {
            loading_bulk_message_lists: false,
            bulk_message_errors: null,
            bulk_message_lists: [],
            bulk_message_dialog: false,
            selected_bulk_message_id: null,
            table_headers: [
                { text: 'Date', value: 'date', sortable: false },
                { text: 'Time', value: 'time', sortable: false },
                { text: 'Imported By', value: 'imported_by', sortable: false },
                { text: 'Original File', value: 'file_name', sortable: false },
                { text: 'Mapped File', value: 'enriched_data', sortable: false },
                { text: 'Status', value: 'state', sortable: false },
                { text: 'Target Bulk Message', value: 'target_bulk_message_names', sortable: false },
                { text: 'Recipients Added', value: 'recipients_added', sortable: false, align: 'center' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],
        };
    },

    computed: {
        footer_props() {
            return {
                itemsPerPageText: 'Imports per page:',
                itemsPerPageOptions: [25, 50, 100],
                disableItemsPerPage: false,
                disablePagination: false
            };
        },
        computed_no_data_text() {
            return 'No bulk recipient imports available';
        },
        bulk_message_options() {
            const options = [];

            // Show loading state
            if (this.loading_bulk_message_lists) {
                options.push({ value: '', text: 'Loading bulk messages...', disabled: true });
                return options;
            }

            // Show error state
            if (this.bulk_message_lists_error) {
                options.push({ value: '', text: 'Error loading bulk messages', disabled: true });
                return options;
            }

            // Add loaded bulk messages
            if (this.bulk_message_lists && this.bulk_message_lists.length > 0) {
                this.bulk_message_lists.forEach(message => {
                    options.push({
                        value: message.bulk_message_id,
                        text: message.label
                    });
                });
            } else {
                options.push({ value: '', text: 'No bulk messages available', disabled: true });
            }

            return options;
        }
    },

    mounted() {
        this.loadBulkMessages();
    },

    methods: {
        hasWriteAccess() {
            // Check if current user has write access to contact list imports
            const organization_id = this.$route.params.organization_id;

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                // RedChirp staff can write in any organization (UI will warn on real accounts)
                return true;
            } else if (this.$rc.$state.isOrganizationAdmin(organization_id)) {
                // Organization admins can write to their organization
                return true;
            }

            // Regular users cannot write
            return false;
        },

        onConfirmListSelection() {
            if (!this.selected_bulk_message_id) {
                return;
            }

            this.bulk_message_dialog = false;

            this.$router.push({
                name: 'bulk-message-recipients-import-upload',
                params: {
                    organization_id: this.$route.params.organization_id
                },
                query: {
                    bulk_message_id: this.selected_bulk_message_id
                }
            });
        },



        onClickStartImport() {
            this.bulk_message_dialog = true;
        },
        
        async loadBulkMessages() {
            if (this.loading_bulk_message_lists) {
                return; // Prevent multiple simultaneous requests
            }

            this.loading_bulk_message_lists = true;
            this.bulk_message_errors = null;

            try {
                this.$rc.rest({
                    spinner: false,
                    endpoint: 'LIST: /api/v1/bulk/messages/search',
                    params: {
                        organization_id: this.$route.params.organization_id,
                        state: "edit",
                        is_archived: false,
                        rc_tag_ids: null
                    },
                    on_success: (data) => {
                        this.bulk_message_lists = data.results || [];
                        this.loading_bulk_message_lists = false;
                        this.bulk_message_errors = null;
                    },
                    on_error: (error) => {
                        this.loading_bulk_message_lists = false;
                        this.bulk_message_errors = 'Failed to load bulk messages';
                        this.bulk_message_lists = [];

                        if (error && error.message) {
                            this.$rc.snackbar(`Failed to load bulk messages: ${error.message}`, 'error');
                        } else {
                            this.$rc.snackbar('Failed to load bulk messages for filtering. Please refresh the page to try again.', 'error');
                        }
                    }
                });
            } catch (error) {
                this.loading_bulk_message_lists = false;
                this.bulk_message_errors = 'Unexpected error loading bulk messages';
                this.bulk_message_lists = [];
                this.$rc.snackbar('An unexpected error occurred while loading bulk messages', 'error');
            }
        },

        onBulkMessageSelected(bulkMessage) {
            this.bulk_message_dialog = false;
            if (!bulkMessage || !bulkMessage.bulk_message_id) {
                return;
            }
            const organization_id = this.$route.params.organization_id;
            const bulk_message_id = bulkMessage.bulk_message_id;
            this.$router.push({
                path: `/app/bulk/messages/${organization_id}/imports/upload`,
                query: { bulk_message_id }
            });
        }
    }
};
</script>

